import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import { FaBell, FaCalendarAlt, FaCheckCircle, FaCircle, FaExclamationCircle, FaTimesCircle, FaCloudDownloadAlt, FaFileExcel, FaFilePdf, FaFilePowerpoint, FaTimes } from "react-icons/fa";
import NavBar from "../Common/NavBar";
import { CinturonesSeguridadDashboardURL, CinturonesSeguridadEventosURL, CinturonesSeguridadExportarURL, VehiculosURL } from "../API/apiurls";
import { useGlobalState } from "../Context/GlobalStateContext";

const KPI_ICONS = {
  cumplimiento: "🛡",
  incorrectos: "⚠",
  riesgo: "👥",
  tendencia: "↗",
};

const REPORT_RANGE_OPTIONS = [
  { value: "HOY", label: "Hoy" },
  { value: "SEMANA", label: "Semana" },
  { value: "MES", label: "Mes" },
  { value: "PERSONALIZADO", label: "Personalizado" },
];

const REPORT_FORMAT_OPTIONS = [
  { value: "PDF", label: "PDF" },
  { value: "PPT", label: "PPT" },
];

const HISTORY_RANGE_OPTIONS = [
  { value: "24H", label: "24H" },
  { value: "7D", label: "7D" },
  { value: "30D", label: "30D" },
  { value: "PERSONALIZADO", label: "Personalizado" },
];

const STATUS_META = {
  OK: { label: "Correcto", dot: "green-dot", badge: "critical", Icon: FaCheckCircle, iconClass: "ok" },
  ADVERTENCIA: { label: "Revisión", dot: "yellow-dot", badge: "warning", Icon: FaExclamationCircle, iconClass: "warning" },
  CRITICO: { label: "Incorrecto", dot: "red-dot", badge: "critical", Icon: FaTimesCircle, iconClass: "critical" },
  SIN_DETECCION: { label: "Sin detección", dot: "gray-dot", badge: "warning", Icon: FaCircle, iconClass: "muted" },
};

const formatDateTime = (value) => {
  if (!value) return "Sin datos";
  const date = Array.isArray(value)
    ? new Date(
        Number(value[0]),
        Number(value[1] || 1) - 1,
        Number(value[2] || 1),
        Number(value[3] || 0),
        Number(value[4] || 0),
        Number(value[5] || 0),
        Math.floor(Number(value[6] || 0) / 1000000)
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin datos";
  return new Intl.DateTimeFormat("es-PE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Lima",
  }).format(date);
};

const formatHourLabel = (hour) => String(hour ?? 0).padStart(2, "0");

const formatPercent = (value) => `${Math.round(Number(value ?? 0))}%`;

const buildLineGeometry = (series) => {
  const width = 520;
  const height = 150;
  const maxValue = Math.max(...series.map((item) => Number(item.total ?? 0)), 1);
  const points = series.map((item, index) => {
    const x = series.length === 1 ? 0 : (index / (series.length - 1)) * width;
    const y = height - (Number(item.total ?? 0) / maxValue) * 110 - 10;
    return { x, y, total: Number(item.total ?? 0), eventos: Number(item.eventos ?? item.total ?? 0), hour: item.hora, label: item.label ?? formatHourLabel(item.hora) };
  });
  const polyline = points.map((point) => `${point.x},${point.y}`).join(" ");
  const area = `M0,150 ${points.map((point) => `L${point.x},${point.y}`).join(" ")} L520,150 Z`;
  const peak = points.reduce((best, point) => (point.total > best.total ? point : best), points[0] || { hour: 0, total: 0 });
  return { points, polyline, area, peak };
};

const normalizeSeverity = (severity) => {
  const value = String(severity || "SIN_DETECCION").trim().toUpperCase();
  return STATUS_META[value] ? value : "SIN_DETECCION";
};

const buildTodayValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildWeekStartValue = () => {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return toDateInputValue(date);
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseInputDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const daysBetween = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86400000));
};

const resolveHistoryRange = (range, customStartDate, customEndDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (range === "7D") {
    const start = addDays(today, -6);
    return { inicio: toDateInputValue(start), fin: toDateInputValue(today), mode: "DAY", label: "7D" };
  }

  if (range === "30D") {
    const start = addDays(today, -29);
    return { inicio: toDateInputValue(start), fin: toDateInputValue(today), mode: "DAY", label: "30D" };
  }

  if (range === "PERSONALIZADO") {
    const start = parseInputDate(customStartDate) || today;
    const end = parseInputDate(customEndDate) || start;
    const inicio = end < start ? end : start;
    const fin = end < start ? start : end;
    return { inicio: toDateInputValue(inicio), fin: toDateInputValue(fin), mode: daysBetween(inicio, fin) > 0 ? "DAY" : "HOUR", label: "Personalizado" };
  }

  return { inicio: toDateInputValue(today), fin: toDateInputValue(today), mode: "HOUR", label: "24H" };
};

const formatHistoryDayLabel = (date) => `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;

const buildHistorySeries = (events, rangeInfo) => {
  if (rangeInfo.mode === "HOUR") {
    const buckets = Array.from({ length: 24 }, (_, hour) => ({ label: formatHourLabel(hour), hour, total: 0, ok: 0 }));
    events.forEach((event) => {
      const date = parseLastUpdate(event?.fechaHora);
      if (!date) return;
      const bucket = buckets[date.getHours()];
      bucket.total += 1;
      if (String(event?.estado || "").trim().toUpperCase() === "ABROCHADO") bucket.ok += 1;
    });
    return buckets.map((bucket) => ({
      label: bucket.label,
      hora: bucket.hour,
      eventos: bucket.total,
      total: bucket.total > 0 ? Math.round((bucket.ok * 10000) / bucket.total) / 100 : 0,
    }));
  }

  const start = parseInputDate(rangeInfo.inicio) || new Date();
  const end = parseInputDate(rangeInfo.fin) || start;
  const totalDays = Math.min(daysBetween(start, end), 60);
  const buckets = new Map();
  for (let offset = 0; offset <= totalDays; offset += 1) {
    const date = addDays(start, offset);
    const key = toDateInputValue(date);
    buckets.set(key, { label: formatHistoryDayLabel(date), total: 0, ok: 0 });
  }

  events.forEach((event) => {
    const date = parseLastUpdate(event?.fechaHora);
    if (!date) return;
    const key = toDateInputValue(date);
    const bucket = buckets.get(key);
    if (!bucket) return;
    bucket.total += 1;
    if (String(event?.estado || "").trim().toUpperCase() === "ABROCHADO") bucket.ok += 1;
  });

  return Array.from(buckets.values()).map((bucket) => ({
    label: bucket.label,
    eventos: bucket.total,
    total: bucket.total > 0 ? Math.round((bucket.ok * 10000) / bucket.total) / 100 : 0,
  }));
};

const resolveReportRange = (range, selectedDate, customStartDate, customEndDate) => {
  const todayValue = buildTodayValue();
  const baseValue = range === "HOY" ? todayValue : selectedDate || todayValue;
  const customStart = customStartDate || todayValue;
  const customEnd = customEndDate || customStart;
  const baseDate = new Date(`${baseValue}T00:00:00`);

  if (range === "SEMANA") {
    const start = new Date(baseDate);
    start.setDate(start.getDate() - 6);
    return { inicio: toDateInputValue(start), fin: toDateInputValue(baseDate), label: "Semana" };
  }

  if (range === "MES") {
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
    return { inicio: toDateInputValue(start), fin: toDateInputValue(end), label: "Mes" };
  }

  if (range === "PERSONALIZADO") {
    return customEnd < customStart
      ? { inicio: customEnd, fin: customStart, label: "Personalizado" }
      : { inicio: customStart, fin: customEnd, label: "Personalizado" };
  }

  return { inicio: todayValue, fin: todayValue, label: "Hoy" };
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const getCabinSeatVisual = (severity) => {
  if (severity === "OK") return { fill: "#193f2b", stroke: "#37d767", chip: "#28c832", glow: "rgba(40, 200, 50, 0.18)" };
  if (severity === "ADVERTENCIA") return { fill: "#45330f", stroke: "#f8b719", chip: "#f8b719", glow: "rgba(248, 183, 25, 0.18)" };
  if (severity === "CRITICO") return { fill: "#4e171d", stroke: "#ff5b64", chip: "#db2329", glow: "rgba(219, 35, 41, 0.18)" };
  return { fill: "#243140", stroke: "#8e9baa", chip: "#8e9baa", glow: "rgba(142, 155, 170, 0.18)" };
};

const parseLastUpdate = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    const date = new Date(
      Number(value[0]),
      Number(value[1] || 1) - 1,
      Number(value[2] || 1),
      Number(value[3] || 0),
      Number(value[4] || 0),
      Number(value[5] || 0),
      Math.floor(Number(value[6] || 0) / 1000000)
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) return date;

  const matchNumeric = String(value).match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})\s+(\d{1,2}):(\d{2})(?:\:(\d{2}))?\s*(AM|PM|a\.m\.?|p\.m\.?|a\.m|p\.m)?/i);
  if (matchNumeric) {
    const day = Number(matchNumeric[1]);
    const month = Number(matchNumeric[2]) - 1;
    const year = Number(matchNumeric[3].length === 2 ? `20${matchNumeric[3]}` : matchNumeric[3]);
    const hourRaw = Number(matchNumeric[4]);
    const minute = Number(matchNumeric[5]);
    const second = Number(matchNumeric[6] || 0);
    const marker = (matchNumeric[7] || "").toLowerCase();
    let hour = hourRaw;
    if (marker.includes("pm") && hour < 12) hour += 12;
    if (marker.includes("am") && hour === 12) hour = 0;
    const parsed = new Date(year, month, day, hour, minute, second);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }

  return null;
};

const normalizeSeatName = (value) => String(value || "").trim().toUpperCase();

const normalizeVehicleType = (vehiculo, estadoCabina = []) => {
  const raw = vehiculo?.tipoVehiculoModel?.codigo || vehiculo?.tipoVehiculo || "";
  const codigo = normalizeSeatName(raw);
  if (codigo) return codigo;
  const total = Number(vehiculo?.cantidadCinturones || estadoCabina.length || 0);
  if (total >= 5) return "CABINA_TRES_LITERAS";
  if ((estadoCabina || []).some((item) => normalizeSeatName(item.codigo || item.posicion).includes("CENTRO"))) return "CABINA_CENTRO";
  return "CABINA_LITERA";
};

const matchSeat = (item, aliases) => {
  const pos = normalizeSeatName(item.posicion);
  const code = normalizeSeatName(item.codigo);
  return aliases.some((alias) => pos.includes(alias) || code.includes(alias));
};

const buildCabinLayout = (estadoCabina, vehiculo) => {
  const tipoVehiculo = normalizeVehicleType(vehiculo, estadoCabina);
  const items = (estadoCabina || []).map((item) => {
    const severity = normalizeSeverity(item.severidad);
    return { ...item, severity, meta: STATUS_META[severity], visual: getCabinSeatVisual(severity), slot: "rear", rearIndex: 0 };
  });

  const assign = (aliases, slot) => {
    const item = items.find((candidate) => !candidate.assigned && matchSeat(candidate, aliases));
    if (item) {
      item.slot = slot;
      item.assigned = true;
    }
    return item;
  };

  assign(["CONDUCTOR"], "front-left");
  assign(["COPILOTO", "PILOTO"], "front-right");

  if (tipoVehiculo === "CABINA_CENTRO") {
    assign(["CENTRO", "TRIPULANTE"], "front-center");
  }

  if (tipoVehiculo === "CABINA_LITERA") {
    assign(["LITERA", "ATRAS"], "rear-center");
  }

  let rearCounter = 0;
  items.forEach((item) => {
    if (!item.assigned) {
      item.slot = "rear";
      item.rearIndex = rearCounter;
      rearCounter += 1;
    }
    delete item.assigned;
  });

  return { tipoVehiculo, items };
};

function CabinFrontSeat({ seat, x, y }) {
  const { visual, meta } = seat;
  return (
    <g>
      <ellipse cx={x + 59} cy={y + 138} rx="58" ry="14" fill={visual.glow} />
      <rect x={x + 12} y={y} width="94" height="56" rx="24" fill={visual.fill} stroke={visual.stroke} strokeWidth="2.5" />
      <rect x={x + 22} y={y + 16} width="74" height="16" rx="8" fill="rgba(255,255,255,0.12)" />
      <rect x={x} y={y + 40} width="118" height="78" rx="22" fill={visual.fill} stroke={visual.stroke} strokeWidth="2.5" />
      <rect x={x + 12} y={y + 58} width="94" height="24" rx="12" fill="rgba(255,255,255,0.1)" />
      <path d={`M ${x + 22} ${y + 118} L ${x + 34} ${y + 148} M ${x + 96} ${y + 118} L ${x + 84} ${y + 148}`} stroke="#6f89a3" strokeWidth="5" strokeLinecap="round" />
      <circle cx={x + 20} cy={y + 102} r="7" fill={visual.chip} />
      <text x={x + 59} y={y + 104} textAnchor="middle" fill="#ffffff" fontSize="15" fontWeight="700">{seat.posicion}</text>
      <text x={x + 59} y={y + 123} textAnchor="middle" fill="#dbeaf8" fontSize="12">{meta.label}</text>
    </g>
  );
}

function CabinCompactSeat({ seat, x, y }) {
  const { visual, meta } = seat;
  return (
    <g>
      <ellipse cx={x + 44} cy={y + 116} rx="42" ry="10" fill={visual.glow} />
      <rect x={x + 7} y={y} width="74" height="48" rx="18" fill={visual.fill} stroke={visual.stroke} strokeWidth="2.2" />
      <rect x={x + 15} y={y + 14} width="58" height="13" rx="7" fill="rgba(255,255,255,0.12)" />
      <rect x={x} y={y + 36} width="88" height="66" rx="18" fill={visual.fill} stroke={visual.stroke} strokeWidth="2.2" />
      <rect x={x + 11} y={y + 52} width="66" height="18" rx="9" fill="rgba(255,255,255,0.1)" />
      <path d={`M ${x + 16} ${y + 102} L ${x + 24} ${y + 126} M ${x + 72} ${y + 102} L ${x + 64} ${y + 126}`} stroke="#6f89a3" strokeWidth="4" strokeLinecap="round" />
      <circle cx={x + 16} cy={y + 87} r="6" fill={visual.chip} />
      <text x={x + 44} y={y + 85} textAnchor="middle" fill="#ffffff" fontSize="13" fontWeight="700">{seat.posicion}</text>
      <text x={x + 44} y={y + 101} textAnchor="middle" fill="#dbeaf8" fontSize="11">{meta.label}</text>
    </g>
  );
}

function CabinRearSeat({ seat, x, y }) {
  const { visual, meta } = seat;
  return (
    <g>
      <ellipse cx={x + 84} cy={y + 98} rx="72" ry="12" fill={visual.glow} />
      <rect x={x} y={y + 18} width="168" height="70" rx="20" fill={visual.fill} stroke={visual.stroke} strokeWidth="2.5" />
      <rect x={x + 14} y={y + 32} width="140" height="24" rx="12" fill="rgba(255,255,255,0.12)" />
      <rect x={x + 18} y={y + 12} width="56" height="20" rx="10" fill="rgba(255,255,255,0.18)" />
      <path d={`M ${x + 18} ${y + 88} L ${x + 18} ${y + 104} M ${x + 150} ${y + 88} L ${x + 150} ${y + 104}`} stroke="#6f89a3" strokeWidth="4" strokeLinecap="round" />
      <circle cx={x + 20} cy={y + 70} r="7" fill={visual.chip} />
      <text x={x + 84} y={y + 66} textAnchor="middle" fill="#ffffff" fontSize="15" fontWeight="700">{seat.posicion}</text>
      <text x={x + 84} y={y + 84} textAnchor="middle" fill="#dbeaf8" fontSize="12">{meta.label}</text>
    </g>
  );
}

function CabinSvg({ estadoCabina, vehiculo }) {
  const { tipoVehiculo, items: layout } = buildCabinLayout(estadoCabina, vehiculo);
  const frontLeft = layout.find((item) => item.slot === "front-left");
  const frontCenter = layout.find((item) => item.slot === "front-center");
  const frontRight = layout.find((item) => item.slot === "front-right");
  const rearCenter = layout.find((item) => item.slot === "rear-center");
  const rearSeats = layout.filter((item) => item.slot === "rear");

  return (
    <svg viewBox="0 0 720 500" role="img" aria-label="Estado actual de la cabina">
      <defs>
        <linearGradient id="cab-shell" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#122c43" /><stop offset="100%" stopColor="#07131e" /></linearGradient>
        <linearGradient id="cab-glass" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4ab0ff" stopOpacity="0.72" /><stop offset="100%" stopColor="#0e3654" stopOpacity="0.24" /></linearGradient>
        <linearGradient id="cab-dashboard" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#17324c" /><stop offset="100%" stopColor="#0a1622" /></linearGradient>
        <linearGradient id="cab-floor" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#18354d" stopOpacity="0.82" /><stop offset="100%" stopColor="#091520" stopOpacity="1" /></linearGradient>
      </defs>
      <path d="M88 48 L632 48 C663 48 688 73 688 104 L688 404 C688 435 663 460 632 460 L88 460 C57 460 32 435 32 404 L32 104 C32 73 57 48 88 48 Z" fill="url(#cab-shell)" stroke="#2b577d" strokeWidth="2" />
      <path d="M144 76 L576 76 C606 76 628 96 632 122 L620 154 L100 154 L88 122 C92 96 114 76 144 76 Z" fill="#15344d" stroke="#2e6997" />
      <path d="M116 92 L332 92 L344 146 L98 146 L106 110 C109 99 116 92 116 92 Z" fill="url(#cab-glass)" stroke="#8bd7ff" strokeOpacity="0.38" />
      <path d="M604 92 L388 92 L376 146 L622 146 L614 110 C611 99 604 92 604 92 Z" fill="url(#cab-glass)" stroke="#8bd7ff" strokeOpacity="0.38" />
      <rect x="355" y="84" width="10" height="68" rx="5" fill="#b5d7ef" opacity="0.74" />
      <path d="M118 162 C172 146, 256 138, 360 138 C464 138, 548 146, 602 162 L574 238 C505 223, 434 214, 360 214 C286 214, 215 223, 146 238 Z" fill="url(#cab-dashboard)" stroke="#2b5f88" />
      <circle cx="210" cy="194" r="28" fill="#0f2333" stroke="#3d7fb0" /><circle cx="210" cy="194" r="19" fill="#173b59" />
      <rect x="314" y="166" width="92" height="32" rx="12" fill="#1d4769" stroke="#4d8fc3" /><rect x="420" y="170" width="104" height="22" rx="11" fill="#142c42" stroke="#2f658f" />
      <path d="M84 250 C160 224, 560 224, 636 250 L652 420 C581 438, 139 438, 68 420 Z" fill="url(#cab-floor)" stroke="#255274" />
      <path d="M300 228 L420 228 L454 428 L266 428 Z" fill="#0a1520" stroke="#244f72" />
      <rect x="320" y="236" width="80" height="156" rx="18" fill="#12283c" stroke="#366d98" /><rect x="336" y="252" width="48" height="96" rx="14" fill="#0d1f2e" stroke="#254a69" />
      <path d="M62 168 L116 168 L106 352 L60 366 Z" fill="#0b1a28" stroke="#264e71" /><path d="M658 168 L604 168 L614 352 L660 366 Z" fill="#0b1a28" stroke="#264e71" />
      <rect x="70" y="306" width="580" height="128" rx="26" fill="#0e2131" stroke="#2a5b81" /><path d="M262 306 L262 434 M458 306 L458 434" stroke="#295a80" strokeWidth="2" strokeDasharray="5 7" />
      {tipoVehiculo === "CABINA_CENTRO" ? (
        <>
          {frontLeft && <CabinCompactSeat seat={frontLeft} x={160} y={168} />}
          {frontCenter && <CabinCompactSeat seat={frontCenter} x={316} y={178} />}
          {frontRight && <CabinCompactSeat seat={frontRight} x={472} y={168} />}
        </>
      ) : (
        <>
          {frontLeft && <CabinFrontSeat seat={frontLeft} x={126} y={146} />}
          {frontRight && <CabinFrontSeat seat={frontRight} x={476} y={146} />}
        </>
      )}
      {rearCenter && <CabinRearSeat seat={rearCenter} x={276} y={330} />}
      {rearSeats.map((seat, index) => <CabinRearSeat key={`${seat.codigo}-${seat.posicion}`} seat={seat} x={rearSeats.length === 1 ? 276 : 92 + (184 * index)} y={330} />)}
    </svg>
  );
}

export function CinturonesSeguridad() {
  const navigate = useNavigate();
  const { userData } = useGlobalState();
  const empresaId = Number(userData?.empresaId || localStorage.getItem("empresaid") || 0) || undefined;
  const empresaNombre = userData?.empresaNombre || localStorage.getItem("empresaNombre") || "tu empresa";

  const [vehiculos, setVehiculos] = useState([]);
  const [vehiculoId, setVehiculoId] = useState("");
  const [startDate, setStartDate] = useState(buildWeekStartValue);
  const [endDate, setEndDate] = useState(buildTodayValue);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [excelRange, setExcelRange] = useState("HOY");
  const [graphicsRange, setGraphicsRange] = useState("HOY");
  const [graphicsFormat, setGraphicsFormat] = useState("PDF");
  const [customReportStartDate, setCustomReportStartDate] = useState(buildTodayValue);
  const [customReportEndDate, setCustomReportEndDate] = useState(buildTodayValue);
  const [historyRange, setHistoryRange] = useState("7D");
  const [historyCustomStartDate, setHistoryCustomStartDate] = useState(buildTodayValue);
  const [historyCustomEndDate, setHistoryCustomEndDate] = useState(buildTodayValue);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!empresaId) return;
    const loadVehiculos = async () => {
      try {
        const response = await axios.get(`${VehiculosURL}/xempresa/${empresaId}`);
        setVehiculos(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error cargando vehículos:", error);
        setVehiculos([]);
      }
    };
    loadVehiculos();
  }, [empresaId]);

  useEffect(() => {
    if (!vehiculos.length) {
      setVehiculoId("");
      return;
    }

    const ids = vehiculos.map((vehiculo) => String(vehiculo.id));
    if (!vehiculoId || !ids.includes(String(vehiculoId))) {
      setVehiculoId(String(vehiculos[0].id));
    }
  }, [vehiculos, vehiculoId]);

  const historyRangeInfo = useMemo(() => resolveHistoryRange(historyRange, historyCustomStartDate, historyCustomEndDate), [historyCustomEndDate, historyCustomStartDate, historyRange]);

  useEffect(() => {
    if (!empresaId) return;
    let active = true;

    const loadHistory = async () => {
      setLoadingHistory(true);
      try {
        const params = {
          empresaId,
          fechaInicio: historyRangeInfo.inicio,
          fechaFin: historyRangeInfo.fin,
        };
        if (vehiculoId) params.vehiculoId = Number(vehiculoId);
        const response = await axios.get(CinturonesSeguridadEventosURL, { params });
        const items = Array.isArray(response.data) ? response.data : Array.isArray(response.data?.content) ? response.data.content : [];
        if (active) setHistoryEvents(items);
      } catch (error) {
        console.error("Error cargando histórico de cinturones:", error);
        if (active) setHistoryEvents([]);
      } finally {
        if (active) setLoadingHistory(false);
      }
    };

    loadHistory();
    return () => {
      active = false;
    };
  }, [empresaId, historyRangeInfo.fin, historyRangeInfo.inicio, vehiculoId]);

  useEffect(() => {
    let active = true;
    let intervalId;

    const loadDashboard = async (showLoader = false) => {
      try {
        if (showLoader) setLoading(true);
        const params = {};
        if (empresaId) params.empresaId = empresaId;
        if (vehiculoId) params.vehiculoId = Number(vehiculoId);
        if (startDate) params.fechaInicio = startDate;
        if (endDate) params.fechaFin = endDate;
        const response = await axios.get(CinturonesSeguridadDashboardURL, { params });
        if (active) setDashboard(response.data || null);
      } catch (error) {
        console.error("Error cargando dashboard de cinturones:", error);
        if (active && showLoader) setDashboard(null);
      } finally {
        if (active && showLoader) setLoading(false);
      }
    };

    loadDashboard(true);
    intervalId = setInterval(() => loadDashboard(false), 2000);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [empresaId, vehiculoId, startDate, endDate]);

  const selectedVehiculo = useMemo(
    () => vehiculos.find((vehiculo) => String(vehiculo.id) === String(vehiculoId)) || null,
    [vehiculos, vehiculoId]
  );
  const eventosPorHora = useMemo(() => dashboard?.eventosPorHora || [], [dashboard?.eventosPorHora]);
  const lineGeometry = useMemo(() => buildLineGeometry(eventosPorHora), [eventosPorHora]);
  const historySeries = useMemo(() => buildHistorySeries(historyEvents, historyRangeInfo), [historyEvents, historyRangeInfo]);
  const historyLineGeometry = useMemo(() => buildLineGeometry(historySeries), [historySeries]);
  const estadoCabina = useMemo(() => dashboard?.estadoCabina || [], [dashboard?.estadoCabina]);
  const ultimasAlertas = useMemo(() => dashboard?.ultimasAlertas || [], [dashboard?.ultimasAlertas]);
  const estadoActual = useMemo(() => dashboard?.estadoActual || {
    total: 0,
    abrochado: 0,
    desabrochado: 0,
    alerta: 0,
    sinDeteccion: 0,
  }, [dashboard?.estadoActual]);

  const positionBars = useMemo(() => {
    if (!estadoCabina.length) return [];
    return estadoCabina.map((item) => {
      const value = Math.max(0, Math.min(100, Math.round(Number(item.porcentajeUsoCorrecto ?? 0))));
      const className = value >= 90 ? "green-bar" : value >= 60 ? "yellow-bar" : "red-bar";
      return {
        label: item.posicion,
        value,
        className,
        totalEventos: Number(item.totalEventos ?? 0),
        eventosCorrectos: Number(item.eventosCorrectos ?? 0),
      };
    });
  }, [estadoCabina]);

  const totalDonut = estadoActual.total || 0;
  const donutCircumference = 2 * Math.PI * 54;
  const donutParts = useMemo(() => [
    {
      label: "Uso correcto",
      value: estadoActual.abrochado || 0,
      percent: totalDonut > 0 ? Math.round(((estadoActual.abrochado || 0) * 100) / totalDonut) : 0,
      className: "donut-green",
      dotClass: "green-dot",
    },
    {
      label: "Uso incorrecto",
      value: estadoActual.desabrochado || 0,
      percent: totalDonut > 0 ? Math.round(((estadoActual.desabrochado || 0) * 100) / totalDonut) : 0,
      className: "donut-yellow",
      dotClass: "yellow-dot",
    },
    {
      label: "Sin cinturón",
      value: estadoActual.alerta || 0,
      percent: totalDonut > 0 ? Math.round(((estadoActual.alerta || 0) * 100) / totalDonut) : 0,
      className: "donut-red",
      dotClass: "red-dot",
    },
    {
      label: "No detectado",
      value: estadoActual.sinDeteccion || 0,
      percent: totalDonut > 0 ? Math.round(((estadoActual.sinDeteccion || 0) * 100) / totalDonut) : 0,
      className: "donut-gray",
      dotClass: "gray-dot",
    },
  ], [estadoActual, totalDonut]);

  const donutSegments = useMemo(() => {
    let consumed = 0;
    return donutParts.map((item) => {
      const length = donutCircumference * (item.percent / 100);
      const segment = {
        ...item,
        dashArray: `${length} ${donutCircumference}`,
        dashOffset: -consumed,
      };
      consumed += length;
      return segment;
    });
  }, [donutCircumference, donutParts]);

  const mayorRiesgo = dashboard?.mayorRiesgo || "Sin riesgo activo";
  const tendenciaSemanal = Number(dashboard?.tendenciaSemanal ?? 0);
  const isRecentUpdate = useMemo(() => {
    const ultimaActualizacion = parseLastUpdate(dashboard?.ultimaActualizacion);
    if (!ultimaActualizacion) return false;
    const tiempo = Date.now() - ultimaActualizacion.getTime();
    const limite = 10 * 60 * 1000;
    return tiempo >= 0 && tiempo <= limite;
  }, [dashboard?.ultimaActualizacion]);

  const descargarReporte = () => {
    setShowReportModal(true);
  };

  const descargarRegistrosExcel = async () => {
    try {
      const range = resolveReportRange(excelRange, endDate, customReportStartDate, customReportEndDate);
      const params = { fechaInicio: range.inicio, fechaFin: range.fin };
      if (empresaId) params.empresaId = empresaId;
      if (vehiculoId) params.vehiculoId = Number(vehiculoId);
      const response = await axios.get(CinturonesSeguridadExportarURL, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/vnd.ms-excel;charset=utf-8;" });
      const suffix = range.inicio === range.fin ? range.inicio : `${range.inicio}-${range.fin}`;
      downloadBlob(blob, `registros-cinturones-${suffix}.xls`);
      setShowReportModal(false);
    } catch (error) {
      console.error("Error exportando reporte de cinturones:", error);
    }
  };

  const buildGraphicsReportHtml = (range) => {
    const unidad = vehiculos.find((vehiculo) => String(vehiculo.id) === String(vehiculoId));
    const barsHtml = positionBars.map((item) => `
      <div class="bar-row"><span>${escapeHtml(item.label)}</span><div class="bar-track"><div class="bar-fill" style="width:${item.value}%"></div></div><strong>${item.value}%</strong></div>
    `).join("");
    const axisHtml = eventosPorHora.map((point) => `<span>${escapeHtml(formatHourLabel(point.hora))}</span>`).join("");
    const circlesHtml = lineGeometry.points.map((point) => `<circle cx="${point.x}" cy="${point.y}" r="4" fill="#1677ff" stroke="#69aaff" stroke-width="2" />`).join("");
    const donutHtml = donutParts.map((item) => `<tr><td>${escapeHtml(item.label)}</td><td>${item.value}</td><td>${item.percent}%</td></tr>`).join("");
    const cabinHtml = estadoCabina.map((item) => `<tr><td>${escapeHtml(item.posicion)}</td><td>${escapeHtml(STATUS_META[normalizeSeverity(item.severidad)]?.label || item.severidad)}</td><td>${Math.round(Number(item.porcentajeUsoCorrecto ?? 0))}%</td><td>${item.totalEventos ?? 0}</td><td>${item.eventosCorrectos ?? 0}</td></tr>`).join("");
    const temporalHtml = eventosPorHora.map((point) => `<tr><td>${formatHourLabel(point.hora)}:00</td><td>${point.total ?? 0}</td></tr>`).join("");
    const alertasHtml = ultimasAlertas.slice(0, 12).map((alerta) => `<tr><td>${escapeHtml(formatDateTime(alerta.fechaHora))}</td><td>${escapeHtml(alerta.vehiculoNombre || "Vehiculo sin nombre")}</td><td>${escapeHtml(alerta.asiento || "Sin posicion")}</td><td>${escapeHtml(alerta.estado || "Sin estado")}</td><td>${escapeHtml(STATUS_META[normalizeSeverity(alerta.severidad)]?.label || alerta.severidad)}</td></tr>`).join("");
    const topVehiculosHtml = (dashboard?.topVehiculos || []).map((item) => `<tr><td>${escapeHtml(item.vehiculoNombre || item.nombre || item.vehiculo || "Sin vehiculo")}</td><td>${item.totalEventos ?? item.eventos ?? item.total ?? 0}</td><td>${item.alertas ?? item.eventosIncorrectos ?? 0}</td></tr>`).join("");

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Reporte de gráficos de cinturones</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #102033; margin: 28px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 15px; margin: 0 0 14px; color: #24435f; }
  .meta { color: #53677a; font-size: 12px; margin-bottom: 18px; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 18px; }
  .kpi { border: 1px solid #c9d7e2; border-radius: 8px; padding: 12px; }
  .kpi span { display:block; color:#53677a; font-size:11px; text-transform:uppercase; }
  .kpi strong { display:block; font-size:24px; margin-top:6px; color:#102033; }
  .section { border: 1px solid #c9d7e2; border-radius: 8px; padding: 14px; margin-bottom: 14px; page-break-inside: avoid; }
  .bar-row { display:grid; grid-template-columns: 120px 1fr 48px; gap:10px; align-items:center; margin: 10px 0; font-size: 12px; }
  .bar-track { height: 18px; background:#eef3f7; border-radius: 10px; overflow:hidden; }
  .bar-fill { height: 100%; background: linear-gradient(90deg, #1677ff, #20c997); }
  svg { width:100%; height:160px; background:#f7fafc; border-radius:8px; }
  .axis { display:flex; justify-content:space-between; color:#53677a; font-size:11px; margin-top:6px; }
  table { width:100%; border-collapse: collapse; font-size: 12px; }
  th, td { border-bottom:1px solid #dce6ee; padding:8px; text-align:left; }
  th { color:#24435f; background:#f4f8fb; }
  @media print { body { margin: 18px; } }
</style>
</head>
<body>
  <h1>Reporte de gráficos de cinturones</h1>
  <div class="meta">Unidad: ${escapeHtml(unidad?.nombre || "Sin unidad")} | Periodo: ${escapeHtml(range.label)} (${range.inicio} / ${range.fin}) | Rango panel: ${escapeHtml(startDate)} / ${escapeHtml(endDate)}</div>
  <div class="grid">
    <div class="kpi"><span>Cumplimiento</span><strong>${formatPercent(dashboard?.cumplimientoGeneral)}</strong></div>
    <div class="kpi"><span>Eventos incorrectos</span><strong>${dashboard?.alertas ?? 0}</strong></div>
    <div class="kpi"><span>Eventos del dia</span><strong>${dashboard?.eventosHoy ?? 0}</strong></div>
    <div class="kpi"><span>Cinturones activos</span><strong>${dashboard?.cinturonesActivos ?? 0}</strong></div>
    <div class="kpi"><span>Vehiculos monitoreados</span><strong>${dashboard?.vehiculosMonitoreados ?? 0}</strong></div>
    <div class="kpi"><span>Mayor riesgo</span><strong>${escapeHtml(mayorRiesgo)}</strong></div>
    <div class="kpi"><span>Tendencia</span><strong>${tendenciaSemanal > 0 ? `+${tendenciaSemanal}` : tendenciaSemanal}%</strong></div>
    <div class="kpi"><span>Ultima actualizacion</span><strong>${escapeHtml(formatDateTime(dashboard?.ultimaActualizacion))}</strong></div>
  </div>
  <div class="section"><h2>Comportamiento de uso correcto por posición</h2>${barsHtml || "Sin datos"}</div>
  <div class="section"><h2>Histórico de eventos por hora</h2><svg viewBox="0 0 520 150" preserveAspectRatio="none"><path d="${lineGeometry.area}" fill="rgba(22,119,255,.18)" /><polyline points="${lineGeometry.polyline}" fill="none" stroke="#1677ff" stroke-width="3" stroke-linejoin="round" stroke-linecap="round" />${circlesHtml}</svg><div class="axis">${axisHtml}</div><table><thead><tr><th>Hora</th><th>Eventos</th></tr></thead><tbody>${temporalHtml || "<tr><td colspan='2'>Sin datos</td></tr>"}</tbody></table></div>
  <div class="section"><h2>Distribución de eventos</h2><table><thead><tr><th>Estado</th><th>Total</th><th>Porcentaje</th></tr></thead><tbody>${donutHtml}</tbody></table></div>
  <div class="section"><h2>Estado actual en la cabina</h2><table><thead><tr><th>Posición</th><th>Estado</th><th>Uso correcto</th><th>Eventos</th><th>Correctos</th></tr></thead><tbody>${cabinHtml}</tbody></table></div>
  <div class="section"><h2>Últimas alertas</h2><table><thead><tr><th>Fecha</th><th>Vehículo</th><th>Posición</th><th>Estado</th><th>Severidad</th></tr></thead><tbody>${alertasHtml || "<tr><td colspan='5'>Sin alertas</td></tr>"}</tbody></table></div>
  <div class="section"><h2>Vehículos con más eventos</h2><table><thead><tr><th>Vehículo</th><th>Total eventos</th><th>Alertas</th></tr></thead><tbody>${topVehiculosHtml || "<tr><td colspan='3'>Sin datos</td></tr>"}</tbody></table></div>
</body>
</html>`;
  };

  const descargarGraficosPdf = (range, suffix) => {
    const unidad = vehiculos.find((vehiculo) => String(vehiculo.id) === String(vehiculoId));
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = 14;

    const ensureSpace = (height) => {
      if (y + height <= 190) return;
      doc.addPage();
      y = 14;
    };

    const sectionTitle = (title) => {
      ensureSpace(12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(16, 32, 51);
      doc.text(title, margin, y);
      y += 8;
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(16, 32, 51);
    doc.text("Reporte de graficos de cinturones", margin, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(83, 103, 122);
    doc.text(`Unidad: ${unidad?.nombre || "Sin unidad"} | Periodo: ${range.label} (${range.inicio} / ${range.fin}) | Rango panel: ${startDate} / ${endDate}`, margin, y);
    y += 12;

    const kpis = [
      ["Cumplimiento", formatPercent(dashboard?.cumplimientoGeneral)],
      ["Eventos incorrectos", String(dashboard?.alertas ?? 0)],
      ["Eventos del dia", String(dashboard?.eventosHoy ?? 0)],
      ["Cinturones activos", String(dashboard?.cinturonesActivos ?? 0)],
      ["Vehiculos monitoreados", String(dashboard?.vehiculosMonitoreados ?? 0)],
      ["Mayor riesgo", mayorRiesgo],
      ["Tendencia", `${tendenciaSemanal > 0 ? `+${tendenciaSemanal}` : tendenciaSemanal}%`],
      ["Ultima actualizacion", formatDateTime(dashboard?.ultimaActualizacion)],
    ];
    const cardWidth = (pageWidth - margin * 2 - 12) / 4;
    kpis.forEach(([label, value], index) => {
      const row = Math.floor(index / 4);
      const col = index % 4;
      const x = margin + col * (cardWidth + 4);
      const cardY = y + row * 24;
      doc.setDrawColor(201, 215, 226);
      doc.setFillColor(247, 250, 252);
      doc.roundedRect(x, cardY, cardWidth, 20, 2, 2, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(83, 103, 122);
      doc.text(label, x + 4, cardY + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(16, 32, 51);
      doc.text(String(value), x + 4, cardY + 15, { maxWidth: cardWidth - 8 });
    });
    y += 58;

    sectionTitle("Comportamiento de uso correcto por posicion");
    if (!positionBars.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Sin datos", margin, y);
      y += 8;
    } else {
      positionBars.forEach((item) => {
        ensureSpace(9);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(16, 32, 51);
        doc.text(item.label, margin, y + 4);
        doc.setFillColor(238, 243, 247);
        doc.roundedRect(margin + 42, y, 120, 5, 2, 2, "F");
        doc.setFillColor(item.value >= 90 ? 32 : item.value >= 60 ? 248 : 219, item.value >= 90 ? 200 : item.value >= 60 ? 183 : 35, item.value >= 90 ? 50 : item.value >= 60 ? 25 : 41);
        doc.roundedRect(margin + 42, y, 120 * item.value / 100, 5, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.text(`${item.value}%`, margin + 168, y + 4);
        y += 8;
      });
      y += 6;
    }

    sectionTitle("Historico de eventos por hora");
    const chartX = margin;
    const chartY = y;
    const chartW = 180;
    const chartH = 42;
    doc.setDrawColor(220, 230, 238);
    doc.setFillColor(247, 250, 252);
    doc.roundedRect(chartX, chartY, chartW, chartH, 2, 2, "FD");
    if (lineGeometry.points.length > 1) {
      const maxTotal = Math.max(...lineGeometry.points.map((point) => point.total), 1);
      doc.setDrawColor(22, 119, 255);
      doc.setLineWidth(0.8);
      lineGeometry.points.forEach((point, index) => {
        if (index === 0) return;
        const prev = lineGeometry.points[index - 1];
        const x1 = chartX + (prev.x / 520) * chartW;
        const y1 = chartY + chartH - (prev.total / maxTotal) * (chartH - 8) - 4;
        const x2 = chartX + (point.x / 520) * chartW;
        const y2 = chartY + chartH - (point.total / maxTotal) * (chartH - 8) - 4;
        doc.line(x1, y1, x2, y2);
      });
    }
    y += chartH + 8;
    sectionTitle("Detalle temporal por hora");
    const hourColumns = [margin, margin + 34, margin + 68, margin + 102, margin + 136, margin + 170, margin + 204, margin + 238];
    eventosPorHora.forEach((point, index) => {
      const col = index % hourColumns.length;
      if (index > 0 && col === 0) y += 6;
      ensureSpace(7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(16, 32, 51);
      doc.text(`${formatHourLabel(point.hora)}:00 ${point.total ?? 0}`, hourColumns[col], y);
    });
    y += 12;

    sectionTitle("Distribucion de eventos");
    donutParts.forEach((item) => {
      ensureSpace(7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(16, 32, 51);
      doc.text(`${item.label}: ${item.value} (${item.percent}%)`, margin, y);
      y += 6;
    });
    y += 4;

    sectionTitle("Estado actual en la cabina");
    estadoCabina.forEach((item) => {
      ensureSpace(7);
      const estado = STATUS_META[normalizeSeverity(item.severidad)]?.label || item.severidad;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(16, 32, 51);
      doc.text(`${item.posicion}: ${estado} | Uso correcto ${Math.round(Number(item.porcentajeUsoCorrecto ?? 0))}% | Eventos ${item.totalEventos ?? 0}`, margin, y);
      y += 6;
    });

    sectionTitle("Ultimas alertas");
    ultimasAlertas.slice(0, 12).forEach((alerta) => {
      ensureSpace(8);
      const severity = STATUS_META[normalizeSeverity(alerta.severidad)]?.label || alerta.severidad;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(16, 32, 51);
      doc.text(`${formatDateTime(alerta.fechaHora)} | ${alerta.vehiculoNombre || "Vehiculo sin nombre"} | ${alerta.asiento || "Sin posicion"} | ${alerta.estado || "Sin estado"} | ${severity}`, margin, y, { maxWidth: pageWidth - margin * 2 });
      y += 6;
    });

    sectionTitle("Vehiculos con mas eventos");
    (dashboard?.topVehiculos || []).forEach((item) => {
      ensureSpace(7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(16, 32, 51);
      doc.text(`${item.vehiculoNombre || item.nombre || item.vehiculo || "Sin vehiculo"}: ${item.totalEventos ?? item.eventos ?? item.total ?? 0} eventos | ${item.alertas ?? item.eventosIncorrectos ?? 0} alertas`, margin, y);
      y += 6;
    });

    doc.save(`graficos-cinturones-${suffix}.pdf`);
  };

  const descargarGraficosReporte = () => {
    const range = resolveReportRange(graphicsRange, endDate, customReportStartDate, customReportEndDate);
    const html = buildGraphicsReportHtml(range);
    const suffix = range.inicio === range.fin ? range.inicio : `${range.inicio}-${range.fin}`;
    if (graphicsFormat === "PPT") {
      const blob = new Blob([html], { type: "application/vnd.ms-powerpoint;charset=utf-8;" });
      downloadBlob(blob, `graficos-cinturones-${suffix}.ppt`);
      setShowReportModal(false);
      return;
    }

    descargarGraficosPdf(range, suffix);
    setShowReportModal(false);
  };

  const abrirAlertas = () => {
    const query = new URLSearchParams();
    if (vehiculoId) query.set("vehiculoId", vehiculoId);
    setShowNotifications(false);
    navigate(`/cinturones-seguridad/alertas?${query.toString()}`);
  };

  return (
    <div className="cinturones-operativos-page">
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          font-family: Arial, Helvetica, sans-serif;
        }

        .cinturones-operativos-page {
          min-height: 100vh;
          background: #071827;
          color: #ffffff;
          padding: 88px 18px 22px;
        }

        .dashboard {
          width: 100%;
          max-width: none;
          margin: 0;
        }

        .top-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .title h1 {
          font-size: 25px;
          font-weight: 800;
          margin-bottom: 4px;
        }

        .title p {
          color: #ffffff;
          font-size: 14px;
        }

        .icon-box {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.35);
          background: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .icon-box.recent {
          background: #22c55e;
          border-color: rgba(34, 197, 94, 0.8);
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.22);
        }

        .icon-box.stale {
          background: #94a3b8;
          border-color: rgba(148, 163, 184, 0.8);
          box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.16);
        }

        .live-refresh-label {
          display: block;
          margin-top: 3px;
          color: #8fd3ff;
          font-size: 11px;
          font-weight: 700;
        }
        .update-box {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .card,
        .card p,
        .card span,
        .card strong,
        .card div,
        .card h1,
        .card h2,
        .card h3,
        .card h4,
        .card h5,
        .card h6 {
          color: #ffffff;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 22px;
          color: #ffffff;
          font-size: 13px;
        }



        .notification {
          position: relative;
        }

        .notification-button {
          position: relative;
          width: 42px;
          height: 42px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.16);
          background: rgba(255, 255, 255, 0.04);
          color: #ffffff;
          font-size: 22px;
          cursor: pointer;
        }

        .notification-icon {
          width: 20px;
          height: 20px;
          color: #ffffff;
        }

        .notification span {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #d9363e;
          color: white;
          font-size: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }

        .notification-panel {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          width: 340px;
          border-radius: 14px;
          border: 1px solid rgba(93, 140, 183, 0.32);
          background: linear-gradient(145deg, rgba(16, 38, 59, 0.98), rgba(8, 24, 40, 0.98));
          box-shadow: 0 18px 42px rgba(0, 0, 0, 0.34);
          padding: 14px;
          z-index: 20;
        }

        .notification-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          font-size: 13px;
          font-weight: 700;
        }

        .notification-panel-list {
          display: grid;
          gap: 8px;
          max-height: 280px;
          overflow-y: auto;
          padding-right: 2px;
        }

        .notification-panel-item {
          display: grid;
          grid-template-columns: 20px 1fr;
          gap: 10px;
          padding: 10px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255,255,255,0.05);
        }

        .notification-panel-item strong {
          display: block;
          font-size: 12px;
          margin-bottom: 3px;
        }

        .notification-panel-item span {
          position: static;
          width: auto;
          height: auto;
          background: transparent;
          font-size: 11px;
          color: #cfe0ef;
          display: block;
          font-weight: 400;
          text-align: left;
        }

        .notification-panel-empty {
          padding: 14px 8px;
          color: #cfe0ef;
          font-size: 12px;
          text-align: center;
        }

        .notification-panel-footer {
          margin-top: 10px;
          text-align: right;
        }

        .filters {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 180px;
          gap: 16px;
          margin-bottom: 20px;
        }

        .filter-field {
          border: 1px solid rgba(91, 143, 190, 0.45);
          border-radius: 8px;
          padding: 8px 14px;
          background: rgba(7, 26, 43, 0.85);
          min-height: 44px;
          position: relative;
        }

        .filter-field label {
          position: absolute;
          top: -8px;
          left: 14px;
          background: #071827;
          padding: 0 6px;
          color: #ffffff;
          font-size: 11px;
        }

        .filter-field select,
        .filter-field input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          color: white;
          font-size: 14px;
          height: 26px;
          margin-top: 3px;
        }

        .filter-field select option {
          background: #071f33;
          color: #ffffff;
        }

        .filter-field select:focus,
        .filter-field select:active {
          background: rgba(7, 26, 43, 0.95);
          color: #ffffff;
        }

        .date-picker-field {
          padding: 0 10px 0 14px;
        }

        .date-picker-control {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 3px;
          width: 100%;
          position: relative;
        }

        .date-picker-control input {
          width: calc(100% - 22px);
          min-height: 26px;
          color: #ffffff;
          cursor: pointer;
        }

        .date-picker-control input::-webkit-calendar-picker-indicator {
          filter: invert(1) brightness(1.4);
          opacity: 0.9;
        }

        .date-picker-icon {
          color: #9fc1dc;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          pointer-events: none;
        }

        .download-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: rgba(16, 67, 118, 0.55);
          border: 1px solid #2d8eff;
          color: white;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          padding: 0 12px;
          height: 44px;
        }

        .download-icon {
          width: 14px;
          height: 14px;
        }

        .report-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 60;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: rgba(0, 0, 0, 0.62);
          backdrop-filter: blur(4px);
        }

        .report-modal {
          width: min(760px, 100%);
          border: 1px solid rgba(93, 140, 183, 0.42);
          border-radius: 10px;
          background: linear-gradient(145deg, rgba(16, 38, 59, 0.98), rgba(8, 24, 40, 0.98));
          box-shadow: 0 22px 60px rgba(0, 0, 0, 0.42);
          color: #ffffff;
          overflow: hidden;
        }

        .report-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 18px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .report-modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 800;
        }

        .report-close-btn {
          width: 34px;
          height: 34px;
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.04);
          color: #ffffff;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .report-modal-body {
          display: grid;
          gap: 14px;
          padding: 18px 20px 20px;
        }

        .report-option-row {
          display: grid;
          grid-template-columns: minmax(240px, 1fr) auto auto auto;
          gap: 14px;
          align-items: center;
          padding: 14px;
          border-radius: 9px;
          border: 1px solid rgba(124, 162, 197, 0.22);
          background: rgba(255, 255, 255, 0.035);
        }

        .report-option-main {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .report-option-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(45, 142, 255, 0.13);
          color: #85c5ff;
          flex-shrink: 0;
        }

        .report-option-main strong {
          display: block;
          font-size: 14px;
          margin-bottom: 3px;
        }

        .report-option-main span {
          display: block;
          color: #d6e4f1;
          font-size: 12px;
        }

        .report-select,
        .report-date-input {
          height: 38px;
          min-width: 142px;
          border: 1px solid rgba(91, 143, 190, 0.45);
          border-radius: 8px;
          background: #071f33;
          color: #ffffff;
          padding: 0 10px;
          outline: none;
        }

        .report-format-group {
          display: flex;
          gap: 6px;
        }

        .report-format-btn {
          height: 38px;
          min-width: 58px;
          border: 1px solid rgba(91, 143, 190, 0.45);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.04);
          color: #ffffff;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 700;
        }

        .report-format-btn.active {
          border-color: #2d8eff;
          background: rgba(45, 142, 255, 0.28);
        }

        .report-action-btn {
          height: 38px;
          border: 1px solid #2d8eff;
          border-radius: 8px;
          background: rgba(16, 67, 118, 0.72);
          color: #ffffff;
          padding: 0 14px;
          cursor: pointer;
          font-weight: 700;
          white-space: nowrap;
        }

        .report-custom-date {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 0 14px 2px;
        }

        .report-custom-date label {
          display: grid;
          gap: 5px;
          min-width: 150px;
          color: #ffffff;
          font-size: 11px;
          font-weight: 700;
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 17px;
          margin-bottom: 18px;
        }

        .card {
          background: linear-gradient(145deg, rgba(16, 38, 59, 0.96), rgba(8, 24, 40, 0.96));
          border: 1px solid rgba(93, 140, 183, 0.32);
          border-radius: 9px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
        }

        .kpi-card {
          min-height: 124px;
          padding: 18px 16px;
          display: grid;
          grid-template-columns: 68px 1fr;
          align-items: center;
          gap: 16px;
          text-align: left;
        }

        .kpi-icon {
          width: 68px;
          height: 68px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
        }

        .green {
          background: radial-gradient(circle, rgba(32, 214, 87, 0.42), rgba(32, 214, 87, 0.15));
          border: 1px solid rgba(32, 214, 87, 0.35);
          color: #36e36a;
        }

        .red {
          background: radial-gradient(circle, rgba(240, 63, 72, 0.42), rgba(240, 63, 72, 0.15));
          border: 1px solid rgba(240, 63, 72, 0.35);
          color: #ff6068;
        }

        .yellow {
          background: radial-gradient(circle, rgba(255, 184, 43, 0.45), rgba(255, 184, 43, 0.15));
          border: 1px solid rgba(255, 184, 43, 0.38);
          color: #ffc54a;
        }

        .blue {
          background: radial-gradient(circle, rgba(48, 190, 255, 0.42), rgba(48, 190, 255, 0.15));
          border: 1px solid rgba(48, 190, 255, 0.35);
          color: #55cfff;
        }

        .kpi-content small {
          font-size: 12px;
          color: #ffffff;
          font-weight: 700;
          display: block;
          margin-bottom: 8px;
        }

        .kpi-content {
          flex: 1;
          min-width: 0;
          text-align: left;
        }

        .kpi-content h2 {
          color: #ffffff;
          font-size: 38px;
          margin-bottom: 6px;
        }

        .kpi-content h3 {
          color: #ffffff;
          font-size: 25px;
          margin-bottom: 8px;
        }

        .kpi-content p {
          color: #d5e0eb;
          font-size: 13px;
          line-height: 1.35;
        }

        .main-grid {
          display: grid;
          grid-template-columns: 43% 57%;
          gap: 18px;
          margin-bottom: 18px;
        }

        .chart-card {
          padding: 18px;
          min-height: 318px;
        }

        .card-title {
          color: #ffffff;
          font-size: 14px;
          font-weight: 800;
          margin-bottom: 18px;
          text-transform: uppercase;
        }

        .bar-row {
          display: grid;
          grid-template-columns: 120px 1fr 52px;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
          font-size: 14px;
        }

        .bar-track {
          height: 22px;
          background: rgba(255, 255, 255, 0.05);
          border-left: 1px solid rgba(255, 255, 255, 0.25);
          position: relative;
        }

        .bar {
          height: 100%;
          border-radius: 0 9px 9px 0;
        }

        .bar.green-bar { background: linear-gradient(90deg, #1c8a2e, #47d756); }
        .bar.yellow-bar { background: linear-gradient(90deg, #d99613, #ffc83d); }
        .bar.red-bar { background: linear-gradient(90deg, #a51926, #f1393e); }

        .axis {
          display: flex;
          justify-content: space-between;
          padding-left: 120px;
          color: #ffffff;
          font-size: 12px;
          margin-top: 8px;
        }

        .axis-label {
          text-align: center;
          color: #ffffff;
          font-size: 12px;
          margin-top: 10px;
        }

        .cabin-card {
          padding: 18px;
          min-height: 280px;
        }

        .cabin-placeholder {
          min-height: 210px;
          max-height: 240px;
          border-radius: 14px;
          border: 1px solid rgba(96, 140, 190, 0.28);
          background: rgba(255, 255, 255, 0.03);
          display: flex;
          justify-content: center;
          align-items: center;
          color: #ffffff;
          font-size: 13px;
          margin-top: 6px;
          overflow: hidden;
          padding: 8px 10px;
        }

        .cabin-placeholder svg {
          width: min(100%, 560px);
          height: auto;
          display: block;
        }

        .legend {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-top: 12px;
          font-size: 12px;
          color: #ffffff;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }

        .dot.green-dot { background: #28c832; }
        .dot.yellow-dot { background: #f8b719; }
        .dot.red-dot { background: #db2329; }
        .dot.gray-dot { background: #8e9baa; }

        .bottom-grid {
          display: grid;
          grid-template-columns: 43% 25.5% 29.5%;
          gap: 18px;
          margin-bottom: 18px;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: 38% 62%;
          gap: 18px;
          margin-bottom: 18px;
        }

        .vehicles-card,
        .map-card {
          min-height: 280px;
          padding: 18px;
        }

        .vehicle-list {
          display: grid;
          gap: 12px;
        }

        .vehicle-item {
          display: grid;
          grid-template-columns: 1fr 84px;
          gap: 12px;
          align-items: center;
        }

        .vehicle-copy strong {
          display: block;
          font-size: 13px;
          margin-bottom: 4px;
        }

        .vehicle-copy span {
          display: block;
          font-size: 11px;
          color: #d5e0eb;
        }

        .vehicle-value {
          text-align: right;
          font-size: 18px;
          font-weight: 800;
        }

        .vehicle-meter {
          margin-top: 8px;
          height: 10px;
          background: rgba(255,255,255,0.06);
          border-radius: 999px;
          overflow: hidden;
        }

        .vehicle-meter > span {
          display: block;
          height: 100%;
          background: linear-gradient(90deg, #2c8ff0, #55cfff);
          border-radius: 999px;
        }

        .map-surface {
          height: 220px;
          border-radius: 14px;
          border: 1px solid rgba(96, 140, 190, 0.28);
          background:
            radial-gradient(circle at 20% 20%, rgba(45, 143, 255, 0.12), transparent 28%),
            radial-gradient(circle at 80% 70%, rgba(255, 176, 32, 0.12), transparent 24%),
            linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
          position: relative;
          overflow: hidden;
        }

        .map-gridline {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 48px 48px;
          pointer-events: none;
        }

        .map-pin {
          position: absolute;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 0 0 6px rgba(255,255,255,0.06);
        }

        .map-pin.OK { background: #28c832; }
        .map-pin.ADVERTENCIA { background: #f8b719; }
        .map-pin.CRITICO { background: #db2329; }
        .map-pin.SIN_DETECCION { background: #8e9baa; }

        .map-summary {
          display: flex;
          gap: 18px;
          flex-wrap: wrap;
          margin-top: 12px;
          font-size: 12px;
        }

        .history-card,
        .donut-card,
        .alerts-card {
          min-height: 248px;
          padding: 18px;
        }

        .card-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
        }

        .tabs {
          display: flex;
          gap: 4px;
        }

        .history-tabs {
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .history-custom-range {
          display: flex;
          gap: 10px;
          margin: -4px 0 10px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        .history-custom-range label {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #ffffff;
          font-size: 11px;
        }

        .history-custom-range input {
          border: 1px solid rgba(124, 162, 197, 0.45);
          background: rgba(255, 255, 255, 0.04);
          color: #ffffff;
          border-radius: 6px;
          padding: 6px 8px;
          color-scheme: dark;
        }

        .tab {
          border: 1px solid rgba(124, 162, 197, 0.45);
          color: #ffffff;
          padding: 7px 9px;
          border-radius: 6px;
          font-size: 11px;
          background: rgba(255, 255, 255, 0.04);
        }

        .tab.active {
          background: rgba(19, 103, 184, 0.8);
          border-color: #2c8ff0;
        }

        .chart-placeholder {
          height: 150px;
          border-radius: 8px;
          border: 1px solid rgba(96, 140, 190, 0.28);
          background:
            linear-gradient(to bottom, transparent 19%, rgba(255,255,255,0.06) 20%, transparent 21%),
            linear-gradient(to bottom, transparent 39%, rgba(255,255,255,0.06) 40%, transparent 41%),
            linear-gradient(to bottom, transparent 59%, rgba(255,255,255,0.06) 60%, transparent 61%),
            linear-gradient(to bottom, transparent 79%, rgba(255,255,255,0.06) 80%, transparent 81%),
            rgba(255, 255, 255, 0.025);
          padding: 14px 12px 10px;
          position: relative;
          overflow: hidden;
        }

        .chart-placeholder svg {
          width: 100%;
          height: 100%;
          overflow: visible;
          display: block;
        }

        .chart-axis-x {
          display: flex;
          justify-content: space-between;
          margin-top: 6px;
          color: #ffffff;
          font-size: 11px;
        }

        .chart-label {
          position: absolute;
          top: 10px;
          right: 12px;
          background: rgba(8, 17, 28, 0.9);
          border: 1px solid rgba(76, 132, 213, 0.25);
          border-radius: 999px;
          padding: 4px 8px;
          font-size: 11px;
          color: #ffffff;
        }

        .donut-layout {
          display: grid;
          grid-template-columns: 130px 1fr;
          gap: 18px;
          align-items: center;
        }

        .donut-placeholder {
          width: 130px;
          height: 130px;
          position: relative;
          display: grid;
          place-items: center;
        }

        .donut-placeholder svg {
          width: 130px;
          height: 130px;
          display: block;
        }

        .donut-center {
          position: absolute;
          text-align: center;
          line-height: 1.05;
        }

        .donut-center strong {
          display: block;
          font-size: 22px;
        }

        .donut-center span {
          display: block;
          font-size: 11px;
          color: #ffffff;
        }

        .donut-green { stroke: #28c832; }
        .donut-yellow { stroke: #f8b719; }
        .donut-red { stroke: #db2329; }
        .donut-gray { stroke: #8e9baa; }

        .stats-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
          font-size: 13px;
        }

        .stat-line {
          display: grid;
          grid-template-columns: 12px 1fr auto;
          align-items: center;
          gap: 8px;
          color: #ffffff;
        }

        .alerts-title-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
        }

        .view-all {
          font-size: 12px;
          color: #56b8ff;
        }

        .ver-mas {
          margin-top: 10px;
          text-align: right;
        }

        .view-more-link {
          background: transparent;
          border: none;
          font-size: 12px;
          color: #56b8ff;
          cursor: pointer;
        }


        .alert-item {
          display: grid;
          grid-template-columns: 28px 1fr auto;
          gap: 10px;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .alert-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
        }

        .status-icon {
          width: 18px;
          height: 18px;
          display: inline-flex;
        }

        .status-icon.ok {
          color: #28c832;
        }

        .status-icon.warning {
          color: #ffc847;
        }

        .status-icon.critical {
          color: #ff626a;
        }

        .status-icon.muted {
          color: #8e9baa;
        }

        .alert-icon.critical {
          border: 1px solid rgba(255, 98, 106, 0.5);
          background: rgba(255, 98, 106, 0.12);
        }

        .alert-icon.warning {
          border: 1px solid rgba(255, 200, 71, 0.5);
          background: rgba(255, 200, 71, 0.12);
        }

        .alerts-list {
          max-height: 165px;
          overflow-y: auto;
          padding-right: 4px;
          margin-right: -4px;
          scrollbar-width: thin;
        }

        .alert-text strong {
          display: block;
          font-size: 13px;
          margin-bottom: 3px;
        }

        .alert-text span {
          font-size: 12px;
          color: #ffffff;
        }

        .badge {
          font-size: 11px;
          padding: 6px 10px;
          border-radius: 6px;
        }

        .badge.critical {
          color: #ff8b91;
          background: rgba(196, 41, 47, 0.25);
        }

        .badge.warning {
          color: #ffd16a;
          background: rgba(188, 132, 24, 0.24);
        }

        .footer-banner {
          min-height: 86px;
          padding: 22px 28px;
          display: grid;
          grid-template-columns: 1.8fr 1fr 1fr 1fr 1.1fr;
          gap: 20px;
          align-items: center;
          overflow: hidden;
          position: relative;
        }

        .footer-message {
          display: flex;
          align-items: center;
          gap: 18px;
          font-size: 18px;
          color: #ffffff;
        }

        .footer-shield {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          border: 1px dashed rgba(157, 190, 222, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.45);
        }

        .footer-info {
          border-left: 1px solid rgba(255, 255, 255, 0.18);
          padding-left: 28px;
        }
        .footer-info strong {
          display: block;
          font-size: 16px;
          margin-bottom: 4px;
        }

        .footer-info span {
          font-size: 13px;
          color: #ffffff;
        }

        .footer-image-space {
          height: 55px;
          border-radius: 8px;
          border: 1px dashed rgba(157, 190, 222, 0.4);
          background: rgba(255, 255, 255, 0.03);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 11px;
        }

        @media (max-width: 1100px) {
          .filters,
          .kpi-grid,
          .main-grid,
          .insights-grid,
          .bottom-grid,
          .footer-banner,
          .top-header {
            grid-template-columns: 1fr;
          }

          .top-header {
            flex-direction: column;
            gap: 16px;
          }

          .header-actions {
            width: 100%;
            justify-content: space-between;
          }

          .report-modal-backdrop {
            align-items: flex-start;
            padding: 96px 14px 18px;
            overflow-y: auto;
          }

          .report-option-row {
            grid-template-columns: 1fr;
          }

          .report-select,
          .report-date-input,
          .report-action-btn {
            width: 100%;
          }

          .report-format-group {
            width: 100%;
          }

          .report-format-btn {
            flex: 1;
          }

          .report-custom-date {
            padding: 0;
            flex-direction: column;
          }

          .report-custom-date label {
            width: 100%;
          }
        }
      `}</style>

      <NavBar />

      <main className="dashboard">
        <header className="top-header">
          <div className="title">
            <h1>Panel de Control de Seguridad Operativa</h1>
            <p>Monitoreo del uso correcto de cinturones en cabinas de reparto de cerveza de {empresaNombre}</p>
          </div>

          <div className="header-actions">
            <div className="update-box">
              <div className={`icon-box ${isRecentUpdate ? "recent" : "stale"}`} />
              <div>
                <div>Última actualización</div>
                <strong>{formatDateTime(dashboard?.ultimaActualizacion)}</strong>
                <small className="live-refresh-label">En vivo · refresca cada 2s</small>
              </div>
            </div>

            <div className="notification">
              <button className="notification-button" type="button" onClick={() => setShowNotifications((value) => !value)}>
                <FaBell className="notification-icon" />
                <span>{dashboard?.ultimasAlertas?.length ?? 0}</span>

                </button>
              {showNotifications && (
                <div className="notification-panel">
                  <div className="notification-panel-header">
                    <strong>Alertas recientes</strong>
                    <span>{ultimasAlertas.length}</span>
                  </div>

                  <div className="notification-panel-list">
                    {ultimasAlertas.length === 0 && <div className="notification-panel-empty">No hay alertas recientes.</div>}
                    {ultimasAlertas.map((alerta) => {
                      const severity = normalizeSeverity(alerta.severidad);
                      const meta = STATUS_META[severity];
                      return (
                        <div className="notification-panel-item" key={`header-${alerta.eventoId}`}>
                          <meta.Icon className={`status-icon ${meta.iconClass}`} />
                          <div>
                            <strong>{alerta.asiento || "Sin posición"} - {alerta.estado}</strong>
                            <span>{alerta.vehiculoNombre || "Vehículo sin nombre"} - {formatDateTime(alerta.fechaHora)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="notification-panel-footer">
                    <button className="view-more-link" type="button" onClick={abrirAlertas}>Ver más</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="filters">
          <div className="filter-field">
            <label>Unidad</label>
            <select value={vehiculoId} onChange={(e) => setVehiculoId(e.target.value)}>
              {vehiculos.map((vehiculo) => (
                <option key={vehiculo.id} value={vehiculo.id}>{vehiculo.nombre}</option>
              ))}
            </select>
          </div>

          <div className="filter-field date-picker-field">
            <label>Fecha inicio</label>
            <div className="date-picker-control">
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                aria-label="Filtrar por fecha de inicio"
              />
              <FaCalendarAlt className="date-picker-icon" />
            </div>
          </div>

          <div className="filter-field date-picker-field">
            <label>Fecha fin</label>
            <div className="date-picker-control">
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                aria-label="Filtrar por fecha de fin"
              />
              <FaCalendarAlt className="date-picker-icon" />
            </div>
          </div>

          <button className="download-btn" type="button" onClick={descargarReporte}>{loading ? "Cargando..." : <><FaCloudDownloadAlt className="download-icon" /><span>Descargar reporte</span></>} </button>
        </section>

        <section className="kpi-grid">
          <article className="card kpi-card">
            <div className="kpi-icon green">{KPI_ICONS.cumplimiento}</div>
            <div className="kpi-content">
              <small>CUMPLIMIENTO GENERAL</small>
              <h2>{formatPercent(dashboard?.cumplimientoGeneral)}</h2>
              <p>Uso correcto del cinturón<br />en el periodo seleccionado</p>
            </div>
          </article>

          <article className="card kpi-card">
            <div className="kpi-icon red">{KPI_ICONS.incorrectos}</div>
            <div className="kpi-content">
              <small>EVENTOS INCORRECTOS</small>
              <h2>{dashboard?.alertas ?? 0}</h2>
              <p>Eventos detectados<br />en el periodo</p>
            </div>
          </article>

          <article className="card kpi-card">
            <div className="kpi-icon yellow">{KPI_ICONS.riesgo}</div>
            <div className="kpi-content">
              <small>MAYOR RIESGO</small>
              <h3>{mayorRiesgo}</h3>
              <p>Posición con más eventos<br />incorrectos</p>
            </div>
          </article>

          <article className="card kpi-card">
            <div className="kpi-icon blue">{KPI_ICONS.tendencia}</div>
            <div className="kpi-content">
              <small>TENDENCIA SEMANAL</small>
              <h2>{tendenciaSemanal > 0 ? `+${tendenciaSemanal}` : tendenciaSemanal}%</h2>
              <p>Mejora estimada en el cumplimiento<br />según actividad reciente</p>
            </div>
          </article>
        </section>

        <section className="main-grid">
          <article className="card chart-card">
            <h3 className="card-title">Comportamiento de uso correcto por posición</h3>

            {positionBars.map((item) => (
              <div className="bar-row" key={item.label}>
                <span>{item.label}</span>
                <div className="bar-track">
                  <div className={`bar ${item.className}`} style={{ width: `${item.value}%` }} />
                </div>
                <strong>{item.value}%</strong>
              </div>
            ))}

            <div className="axis">
              <span>0%</span>
              <span>20%</span>
              <span>40%</span>
              <span>60%</span>
              <span>80%</span>
              <span>100%</span>
            </div>

            <div className="axis-label">Porcentaje de uso correcto</div>
          </article>

          <article className="card cabin-card">
            <h3 className="card-title">Estado actual en la cabina</h3>

            <div className="cabin-placeholder">
              <CabinSvg estadoCabina={estadoCabina} vehiculo={selectedVehiculo} />
            </div>

            <div className="legend">
              <div className="legend-item"><span className="dot green-dot" />Correcto</div>
              <div className="legend-item"><span className="dot yellow-dot" />Revisión</div>
              <div className="legend-item"><span className="dot red-dot" />Incorrecto</div>
              <div className="legend-item"><span className="dot gray-dot" />Sin detección</div>
            </div>
          </article>
        </section>

        <section className="bottom-grid">
          <article className="card history-card">
            <div className="card-header-row">
              <h3 className="card-title">Histórico de cumplimiento general</h3>

              <div className="tabs history-tabs">
                {HISTORY_RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    className={`tab ${historyRange === option.value ? "active" : ""}`}
                    type="button"
                    onClick={() => setHistoryRange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {historyRange === "PERSONALIZADO" && (
              <div className="history-custom-range">
                <label><span>Inicio</span><input type="date" value={historyCustomStartDate} onChange={(e) => setHistoryCustomStartDate(e.target.value)} /></label>
                <label><span>Fin</span><input type="date" value={historyCustomEndDate} onChange={(e) => setHistoryCustomEndDate(e.target.value)} /></label>
              </div>
            )}

            <div className="chart-placeholder">
              <div className="chart-label">{historyLineGeometry.peak?.label ?? "Sin datos"} - {Math.round(historyLineGeometry.peak?.total ?? 0)}% · {historyLineGeometry.peak?.eventos ?? 0} eventos{loadingHistory ? " · cargando" : ""}</div>
              <svg viewBox="0 0 520 150" preserveAspectRatio="none" aria-label="Cumplimiento histórico">
                <defs>
                  <linearGradient id="hourArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1677ff" stopOpacity="0.42" />
                    <stop offset="100%" stopColor="#1677ff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={historyLineGeometry.area} fill="url(#hourArea)" />
                <polyline points={historyLineGeometry.polyline} fill="none" stroke="#1677ff" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                {historyLineGeometry.points.map((point, index) => (
                  <circle key={`${point.label}-${index}-${point.total}`} cx={point.x} cy={point.y} r={point.total === historyLineGeometry.peak?.total ? 5 : 4} fill="#1677ff" stroke="#69aaff" strokeWidth="2" />
                ))}
              </svg>
            </div>

            <div className="chart-axis-x">
              {historySeries.map((point, index) => (
                <span key={`${point.label}-${index}`}>{point.label}</span>
              ))}
            </div>
          </article>

          <article className="card donut-card">
            <h3 className="card-title">Distribución de eventos</h3>

            <div className="donut-layout">
              <div className="donut-placeholder">
                <svg viewBox="0 0 130 130" aria-label="Distribución de eventos">
                  <circle cx="65" cy="65" r="54" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="18" />
                  {donutSegments.map((segment) => (
                    <circle
                      key={segment.label}
                      cx="65"
                      cy="65"
                      r="54"
                      fill="none"
                      className={segment.className}
                      strokeWidth="18"
                      strokeDasharray={segment.dashArray}
                      strokeDashoffset={segment.dashOffset}
                      transform="rotate(-90 65 65)"
                      strokeLinecap="round"
                    />
                  ))}
                </svg>
                <div className="donut-center">
                  <strong>{totalDonut}</strong>
                  <span>Total</span>
                </div>
              </div>
              <div className="stats-list">
                {donutParts.map((item) => (
                  <div className="stat-line" key={item.label}>
                    <span className={`dot ${item.dotClass}`} />
                    <span>{item.label}</span>
                    <strong>{item.value} ({item.percent}%)</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <article className="card alerts-card">
            <div className="alerts-title-row">
              <h3 className="card-title">Últimas alertas</h3>
              <span className="view-all">{ultimasAlertas.length}</span>
            </div>

            <div className="alerts-list">
              {ultimasAlertas.map((alerta) => {
                const severity = normalizeSeverity(alerta.severidad);
                const className = severity === "CRITICO" ? "critical" : "warning";
                const statusMeta = STATUS_META[severity];
                const label = severity === "CRITICO" ? "Crítico" : "Advertencia";
                return (
                  <div className="alert-item" key={alerta.eventoId}>
                    <div className={`alert-icon ${className}`}>
                      <statusMeta.Icon className={`status-icon ${statusMeta.iconClass}`} />
                    </div>
                    <div className="alert-text">
                      <strong>{alerta.asiento || "Sin posición"} - {alerta.estado}</strong>
                      <span>{alerta.vehiculoNombre || "Vehículo sin nombre"} - {formatDateTime(alerta.fechaHora)}</span>
                    </div>
                    <span className={`badge ${className}`}>{label}</span>
                  </div>

                );
              })}
            </div>
          
            <div className="ver-mas">
              <button className="view-more-link" type="button" onClick={abrirAlertas}>Ver más</button>
            </div>
          </article>
        </section>

        {showReportModal && (
          <div className="report-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="report-modal-title">
            <div className="report-modal">
              <div className="report-modal-header">
                <h3 id="report-modal-title">Descargar reporte</h3>
                <button className="report-close-btn" type="button" onClick={() => setShowReportModal(false)} aria-label="Cerrar">
                  <FaTimes />
                </button>
              </div>

              <div className="report-modal-body">
                <div className="report-option-row">
                  <div className="report-option-main">
                    <div className="report-option-icon"><FaFileExcel /></div>
                    <div>
                      <strong>Registros generales en Excel</strong>
                      <span>Exporta los eventos registrados por periodo.</span>
                    </div>
                  </div>
                  <select className="report-select" value={excelRange} onChange={(e) => setExcelRange(e.target.value)}>
                    {REPORT_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <button className="report-action-btn" type="button" onClick={descargarRegistrosExcel}>Descargar</button>
                </div>

                {excelRange === "PERSONALIZADO" && (
                  <div className="report-custom-date">
                    <label><span>Inicio</span><input className="report-date-input" type="date" value={customReportStartDate} onChange={(e) => setCustomReportStartDate(e.target.value)} /></label>
                    <label><span>Fin</span><input className="report-date-input" type="date" value={customReportEndDate} onChange={(e) => setCustomReportEndDate(e.target.value)} /></label>
                  </div>
                )}

                <div className="report-option-row">
                  <div className="report-option-main">
                    <div className="report-option-icon"><FaCloudDownloadAlt /></div>
                    <div>
                      <strong>Descargar gráficos de reporte</strong>
                      <span>Exporta los gráficos visibles del panel actual.</span>
                    </div>
                  </div>
                  <div className="report-format-group">
                    {REPORT_FORMAT_OPTIONS.map((option) => {
                      const FormatIcon = option.value === "PDF" ? FaFilePdf : FaFilePowerpoint;
                      return (
                        <button
                          className={`report-format-btn ${graphicsFormat === option.value ? "active" : ""}`}
                          key={option.value}
                          type="button"
                          onClick={() => setGraphicsFormat(option.value)}
                        >
                          <FormatIcon /> {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <select className="report-select" value={graphicsRange} onChange={(e) => setGraphicsRange(e.target.value)}>
                    {REPORT_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <button className="report-action-btn" type="button" onClick={descargarGraficosReporte}>Descargar</button>
                </div>

                {graphicsRange === "PERSONALIZADO" && (
                  <div className="report-custom-date">
                    <label><span>Inicio</span><input className="report-date-input" type="date" value={customReportStartDate} onChange={(e) => setCustomReportStartDate(e.target.value)} /></label>
                    <label><span>Fin</span><input className="report-date-input" type="date" value={customReportEndDate} onChange={(e) => setCustomReportEndDate(e.target.value)} /></label>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <footer className="card footer-banner">
          <div className="footer-message">
            <div className="footer-shield">IMG</div>
            <span>Seguridad que se mide, seguridad que mejora.</span>
          </div>

          <div className="footer-info">
            <strong>{dashboard?.eventosHoy ?? 0} eventos</strong>
            <span>Registrados hoy</span>
          </div>

          <div className="footer-info">
            <strong>{dashboard?.vehiculosMonitoreados ?? 0} vehículos</strong>
            <span>Con actividad</span>
          </div>

          <div className="footer-info">
            <strong>{dashboard?.cinturonesActivos ?? 0} cinturones</strong>
            <span>Activos en catálogo</span>
          </div>

          <div className="footer-image-space">API dashboard en vivo</div>
        </footer>
      </main>
    </div>
  );
}
